/**
 * Small shared pieces for the Business Development screens.
 */
import React, { useEffect, useId, useRef } from 'react';
import { X } from 'lucide-react';
import { Stage, STAGE_LABEL } from './bdApi';
import FilledStatusChip from '../../../components/status/FilledStatusChip';
import { SNF_FACILITIES_STATUS, MONDAY_BLANK } from '../../../theme/mondayPalette';

/**
 * A right-hand drawer with dialog semantics, hand-built rather than the kit's
 * Radix Sheet: Radix Dialog imports a package subpath this project's Jest
 * cannot resolve, and no eMerge workforce screen depends on it.
 *
 * Escape and the backdrop close it; focus moves in on open and back to where
 * it was on close; the page behind does not scroll while it is open.
 */
export function Drawer({ open, onClose, title, description, children }: {
  open: boolean; onClose: () => void; title: React.ReactNode; description?: React.ReactNode; children: React.ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const titleId = useId();
  const descId = useId();

  // Depends on `open` only: re-running on every render would pull focus out of
  // whatever field the rep is typing in.
  useEffect(() => {
    if (!open) return undefined;
    const before = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeRef.current(); };
    document.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      before?.focus?.();
    };
  }, [open]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/40" aria-hidden onClick={() => closeRef.current()} />
      <div
        ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={description ? descId : undefined} tabIndex={-1}
        className="absolute right-0 top-0 h-full w-full sm:max-w-2xl overflow-y-auto bg-background shadow-xl focus:outline-none"
      >
        <div className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b bg-background px-4 py-3">
          <div className="min-w-0">
            <h2 id={titleId} className="text-lg font-semibold break-words">{title}</h2>
            {description && <p id={descId} className="text-sm text-muted-foreground">{description}</p>}
          </div>
          <button type="button" onClick={() => closeRef.current()} aria-label="Close" className="rounded p-1 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <X className="w-4 h-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/**
 * The TM board's Status words (SNF Facilities) carry Monday's colours; a stage
 * the board has no word for (Prospect, BAA sent, BAA signed) takes Monday's
 * neutral grey rather than an invented hue, and the word says which it is.
 */
const STAGE_BOARD_WORD: Partial<Record<Stage, string>> = {
  COLD: 'COLD', INTERESTED: 'INTERESTED', ACTIVE: 'ACTIVE', DROPPED: 'DROPPED', DO_NOT_CONTACT: 'DONT BUG',
};

export function StagePill({ stage, board }: { stage: Stage; board?: Stage | null }) {
  const disagrees = board && board !== stage;
  return (
    <span className="inline-flex items-center gap-1">
      <FilledStatusChip
        word={STAGE_BOARD_WORD[stage] || stage}
        hex={STAGE_BOARD_WORD[stage] ? undefined : MONDAY_BLANK.hex}
        column={SNF_FACILITIES_STATUS}
        label={STAGE_LABEL[stage]}
        size="sm"
      />
      {disagrees && (
        <span className="text-[11px] text-muted-foreground" title="What the TM board says">
          board: {STAGE_LABEL[board as Stage]}
        </span>
      )}
    </span>
  );
}

export function NativeSelect({
  label, value, onChange, children, className = '', hideLabel = false,
}: {
  label: string; value: string; onChange: (v: string) => void; children: React.ReactNode; className?: string; hideLabel?: boolean;
}) {
  const id = React.useId();
  return (
    <label htmlFor={id} className={`flex flex-col gap-1 text-xs text-muted-foreground ${className}`}>
      <span className={hideLabel ? 'sr-only' : ''}>{label}</span>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {children}
      </select>
    </label>
  );
}

export function Stat({ label, value, sub }: { label: string; value: React.ReactNode; sub?: React.ReactNode }) {
  return (
    <div className="rounded-lg border bg-card px-4 py-3">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-2xl font-semibold leading-tight" style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

/** Loading, failure and empty are three different sentences. */
export function StateLine({ loading, error, empty, onRetry }: { loading?: boolean; error?: string | null; empty?: string | null; onRetry?: () => void }) {
  if (loading) return <p role="status" className="py-6 text-sm text-muted-foreground">Loading…</p>;
  if (error) {
    return (
      <div role="alert" className="flex items-center gap-3 rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm">
        <span>{error}</span>
        {onRetry && <button type="button" onClick={onRetry} className="underline underline-offset-2">Try again</button>}
      </div>
    );
  }
  if (empty) return <p className="py-6 text-sm text-muted-foreground">{empty}</p>;
  return null;
}
